import { collection, doc, getDoc, getDocs } from "firebase/firestore";
import { deleteObject, getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { generateAnnualMaskMedicalCertBlob, getAnnualMaskMedicalCertFileName } from "../Export/ExportTab/annualMaskMedicalCert";
import { isValidSenderEmail, normalizeSenderEmail } from "./annualMaskEmailSender";
import { queueAnnualMaskEmail } from "./queueAnnualMaskEmail";
import { canManageAnnualMaskPurchase } from "./annualMaskPurchaseUtils";
import { normalizeSleepStudyReportDocs } from "./cpapFollowupFileUtils";
import { isAnnualMaskPdfFile, sortAnnualMaskFilesByNewest } from "./annualMaskClinicalDataUtils";

const reportAttachment = async (fileStorage, file, label, fallbackFileName) => {
  if (!file || (!file.storagePath && !file.url)) throw new Error(`Upload a ${label} before sending the test email.`);
  const path = file.storagePath
    ? await getDownloadURL(ref(fileStorage, file.storagePath))
    : file.url;
  if (!/^https:\/\//i.test(path || "")) throw new Error(`${label} is unavailable.`);
  return {
    filename: file.fileName || file.savedFileName || fallbackFileName,
    path,
    contentType: file.contentType || "application/pdf",
  };
};

export const canSendAnnualMaskTestEmail = (user) => canManageAnnualMaskPurchase(user) && normalizeSenderEmail(user && user.email) === "admin888@mail.com";

export const queueAnnualMaskTestEmail = async ({ database, fileStorage, currentUser, caseItem }) => {
  if (!canSendAnnualMaskTestEmail(currentUser)) throw new Error("This test is available only to admin888@mail.com with the super_admin or admin role.");
  if (!caseItem || !caseItem.refPath) throw new Error("Save and select an Annual Mask case first.");
  const caseRef = doc(database, caseItem.refPath);
  const snapshot = await getDoc(caseRef);
  if (!snapshot.exists()) throw new Error("This Annual Mask case no longer exists.");
  const current = { ...snapshot.data(), refPath: caseItem.refPath };
  const recipient = String(current.doctorEmail || "").trim();
  if (!isValidSenderEmail(recipient)) throw new Error("The assigned doctor has no valid email address.");
  if (!current.hn) throw new Error("The case is missing its patient HN.");
  const usageAttachment = await reportAttachment(fileStorage, current.usageReport, "CPAP Usage Report", "CPAP-Usage-Report.pdf");
  let sleepStudyReport = current.sleepStudyReport;
  if (!sleepStudyReport || (!sleepStudyReport.storagePath && !sleepStudyReport.url)) {
    const reports = await getDocs(collection(database, "patients", current.hn, "report"));
    sleepStudyReport = sortAnnualMaskFilesByNewest(
      normalizeSleepStudyReportDocs(reports.docs, current.hn).filter(isAnnualMaskPdfFile),
    )[0];
  }
  const sleepStudyAttachment = await reportAttachment(fileStorage, sleepStudyReport, "Sleep Study Report", "Sleep-Study-Report.pdf");
  const patient = current.patient || current;
  const values = current.certificateData || {};
  const blob = await generateAnnualMaskMedicalCertBlob({
    patient,
    nodeRef: current.nodeRef || current.node,
    doctor: current.doctorName,
    ahi: values.ahi,
    minSpo2: values.minSpo2,
    minPressure: values.minPressure,
    maxPressure: values.maxPressure,
    pressure: values.pressure,
    latestSleepStudyDate: values.latestSleepStudyDate,
  });
  const casePatientName = [patient.firstnameTh || patient.firstname, patient.lastnameTh || patient.lastname].map((value) => String(value || "").trim()).filter(Boolean).join(" ") || current.hn;
  const mailRef = doc(collection(database, "mail"));
  const fileName = getAnnualMaskMedicalCertFileName(patient);
  const fileRef = ref(fileStorage, `annual-mask-purchases/${current.hn}/test-email/${mailRef.id}/${fileName}`);
  const contentType = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
  let uploaded = false;
  try {
    await uploadBytes(fileRef, blob, { contentType });
    uploaded = true;
    const url = await getDownloadURL(fileRef);
    const latest = await getDoc(caseRef);
    if (!latest.exists() || latest.data().doctorEmail !== current.doctorEmail || latest.data().doctorName !== current.doctorName || latest.data().hn !== current.hn) {
      throw new Error("The case assignment changed. Refresh before sending the test email.");
    }
    await queueAnnualMaskEmail({
      caseItem: current,
      isTest: true,
      message: {
          subject: `Annual 1 Year Mask Purchase - ${casePatientName} (${current.hn})`,
          html: `
            <h3>Annual 1 Year Mask Purchase</h3>
            <p><strong>Patient:</strong> ${casePatientName}</p>
            <p><strong>HN:</strong> ${current.hn}</p>
            <p><strong>Doctor:</strong> ${current.doctorName || "-"}</p>
            <p>Please review and sign the attached Annual 1 Year Mask Purchase medical certificate.</p>
            <p>Attached: the generated medical certificate, the CPAP Usage Report, and the Sleep Study Report.</p>
          `,
        attachments: [{ filename: fileName, path: url, contentType }, usageAttachment, sleepStudyAttachment],
      },
    });
    return "pisanu@braindynamics.co.th";
  } catch (error) {
    if (uploaded) {
      try { await deleteObject(fileRef); }
      catch (cleanupError) { console.warn("Unable to clean up Annual Mask test document", cleanupError); }
    }
    throw error;
  }
};
